import React from 'react';
import { alpha, styled } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import { useAppSelector } from 'src/hooks/store';
import { useResponsive } from 'src/hooks/responsive';
import { selectConfig } from 'src/store/config/selectors';
import Autocomplete from './Autocomplete';
import { useSearch } from '../Context';
import { FormValues } from '../types';
import { FiltersIcon } from '..';
import Icon from 'src/components/common/Icon';

const COLUMN_WIDTH = 227;

const Wrap = styled(Box, { label: 'Wrap' })(({ theme }) => ({
  position: 'static',

  '@media (max-width: 767px)': {
    position: 'fixed',
    zIndex: 130,
    bottom: 0,
    right: 0,
    width: '100%',
  },
}));

const Underlay = styled(Box, { label: 'Wrap' })(({ theme }) => ({
  '@media (max-width: 767px)': {
    display: 'block',
    content: "''",
    position: 'fixed',
    zIndex: 1,
    top: 0,
    bottom: 0,
    right: 0,
    width: '100%',
    backgroundColor: 'rgba(4,71,109,0.5)',
  },
}));

const Container = styled(Box, { label: 'FiltersContainer' })(({ theme }) => ({
  position: 'relative',
  marginTop: '6px',
  padding: '17px 15px 45px 15px',
  border: `1px solid ${theme.palette.other.formBorderLight}`,
  borderRadius: '10px',
  backgroundColor: theme.palette.background.paper,

  '@media (max-width: 767px)': {
    width: '100%',
    borderRadius: '0',
    margin: '0',
    borderColor: '#0535504c',
    zIndex: 2,
  },
}));

const MobileHeader = styled(Box, { label: 'MobileHeader' })(({ theme }) => ({
  display: 'none',
  position: 'relative',
  width: '100%',
  height: '49px',
  background: '#fff',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 10px',
  paddingTop: '2px',
  borderTopLeftRadius: '10px',
  borderTopRightRadius: '10px',

  '@media (max-width: 767px)': {
    display: 'flex',
    zIndex: 2,
  },
}));

const MobileHeaderLabel = styled(Box, { label: 'MobileHeader' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.primary.main,
  fontSize: '24px',
  fontFamily: 'Yonit-Bold',
  gap: '5px',
}));

const CloseIconWrap = styled(Box, { label: 'CloseIcon' })(({ theme }) => ({
  position: 'absolute',
  right: '10px',
  top: '10px',
}));

const FlexWrapper = styled(Box, { label: 'FiltersFlexWrapper' })(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '10px',

  '@media (max-width: 767px)': {
    flexDirection: 'column',
    gap: '0px',
  },
}));

const AutocompleteWrap = styled(Box, { label: 'FiltersFlexWrapper' })(({ theme }) => ({
  width: `${COLUMN_WIDTH}px`,

  '@media (max-width: 1000px)': {
    width: '175px',
  },

  '@media (max-width: 767px)': {
    width: '95%',
  },
}));

const ButtonsFlexWrapper = styled(Box, { label: 'ButtonsFlexWrapper' })(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '20px',

  '@media (max-width: 767px)': {
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: '25px',

    '.css-i5o7ls-ResetBtn': {
      display: 'none',
    },
  },
}));

const ApplyBtn = styled('button', { label: 'ApplyBtn' })(({ theme }) => ({
  cursor: 'pointer',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: `${COLUMN_WIDTH}px`,
  height: '39px',
  padding: '0',
  border: 'none',
  borderRadius: '5px',
  fontSize: '16px',
  lineHeight: '20.8px',
  fontWeight: '600',
  color: '#FFFFFF',
  backgroundColor: theme.palette.primary.light,
  transition: 'background-color 0.3s',

  '&:hover': {
    backgroundColor: 'alpha(theme.palette.primary.light, 0.8)',
  },

  '&:disabled': {
    pointerEvents: 'none',
    backgroundColor: alpha(theme.palette.primary.light, 0.5),
  },

  '@media (max-width: 767px)': {
    width: '95%',
  },
}));

const ResetBtn = styled('button', { label: 'ResetBtn' })(({ theme }) => ({
  cursor: 'pointer',
  position: 'relative',
  padding: '0',
  border: 'none',
  backgroundColor: 'transparent',
  fontSize: '16px',
  lineHeight: '20.8px',
  fontWeight: '500',
  whiteSpace: 'nowrap',
  textDecoration: 'underline',
  color: theme.palette.primary.main,

  '&:hover': {
    textDecoration: 'none',
  },

  '&:disabled': {
    display: 'none',
  },

  '@media (max-width: 767px)': {
    fontFamily: 'Yonit-Medium',
    position: 'absolute',
    top: '15px',
    left: '10px',
  },
}));

const FilterLabel = styled(Typography, { label: 'FilterLabel' })(({ theme }) => ({
  fontSize: '16px',
  lineHeight: '17.5px',
  fontWeight: '400',
  marginBottom: '4px',
  color: theme.palette.primary.main,
}));

type Props = {
  onClose?: () => void;
};

const Filters: React.FC<Props> = ({ onClose }) => {
  const [isMobile] = useResponsive('MOBILE');
  const config = useAppSelector(selectConfig);
  const search = useSearch();
  const t = config.texts.website.main.header.search.filter;
  const hasFilters = Object.values(search.formik.values.filters).some((filter) => filter !== null);

  const options = React.useMemo(() => {
    const battle = Object.values(config.battles)
      .map((battle: any) => ({
        value: battle._id,
        title: battle.title,
        category: battle.categoryTitle,
      }))
      .sort((a, b) => {
        const categoryComparisson = b.category.localeCompare(a.category);
        const titleComparisson = b.title.localeCompare(a.title);

        return categoryComparisson * 100 + titleComparisson;
      }) satisfies FormValues['filters']['battle'][];

    const categories = new Set(battle.map((battle) => battle.category));
    categories.forEach((category) => {
      const noCertainBattleLabel = `ללא קרב ספציפי`;
      const noCertainBattleItemIndex = battle.findIndex(
        (battle) => battle.title === noCertainBattleLabel && battle.category === category,
      );
      const firstCategoryItemIndex = battle.findIndex((battle) => battle.category === category);

      if (firstCategoryItemIndex === -1 || noCertainBattleItemIndex === -1) {
        return;
      }

      // place noCertainBattleLabel item at the beginning of the category:
      const noCertainBattleItem = battle.splice(noCertainBattleItemIndex, 1)[0];
      battle.splice(firstCategoryItemIndex, 0, noCertainBattleItem);
    });

    return {
      battle,
      unit: Object.values(config.units)
        .map((unit: any) => ({
          value: unit._id,
          title: unit.title,
          category: unit.categoryTitle === 'Unknown Category' ? '' : unit.categoryTitle,
        }))
        .sort((a, b) => {
          const categoryComparisson = b.category.localeCompare(a.category);
          const titleComparisson = b.title.localeCompare(a.title);

          return categoryComparisson * 100 + titleComparisson;
        }) satisfies FormValues['filters']['unit'][],
      division: Object.values(config.divisions)
        .map((division: any) => ({
          value: division._id,
          title: division.title,
        }))
        .sort((a, b) => b.title.localeCompare(a.title)) satisfies FormValues['filters']['division'][],
      soldier: Object.values(config.soldiers)
        .map((soldier: any) => ({
          value: soldier._id,
          name: soldier.name,
          rank: soldier.rankTitle === 'Unknown Rank' ? '' : soldier.rankTitle,
        }))
        .sort((a, b) => b.rank.localeCompare(a.rank)) satisfies FormValues['filters']['name'][],
    };
  }, [config]);

  const onApplyBtnClick = React.useCallback(() => {
    search.formik.setFieldValue('page', 1);
    search.formik.submitForm();
    if (onClose && isMobile) {
      onClose();
    }
  }, [search]);

  const onResetBtnClick = React.useCallback(() => {
    search.formik.setFieldValue('filters.battle', null);
    search.formik.setFieldValue('filters.unit', null);
    search.formik.setFieldValue('filters.division', null);
    search.formik.setFieldValue('filters.name', null);
    search.formik.setFieldValue('page', 1);
    search.formik.submitForm();
  }, [search]);

  const onBgClick = () => {
    if (onClose && isMobile) {
      onClose();
    }
  };

  return (
    <Wrap>
      <Underlay onClick={onBgClick} />
      <MobileHeader>
        <CloseIconWrap onClick={onClose}>
          <Icon type="close" />
        </CloseIconWrap>
        <MobileHeaderLabel>
          <FiltersIcon />
          {t.mobileHeaderLabel}
        </MobileHeaderLabel>
        {hasFilters && (
          <ResetBtn type="button" onClick={onResetBtnClick}>
            {t.resetBtnLabel}
          </ResetBtn>
        )}
      </MobileHeader>
      <Container>
        <FlexWrapper>
          <AutocompleteWrap>
            <FilterLabel>{t.battleLabel}</FilterLabel>
            <Autocomplete
              name="filters.battle"
              clearIcon={null}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              options={options.battle}
              getOptionLabel={(option) => (option as (typeof options.battle)[number]).title}
              groupBy={(option) => option.category}
              renderOption={(props, option) => <li {...props}>{option.title}</li>}
              renderInput={(params) => <TextField {...params} placeholder={t.battlePlaceholder} />}
            />
          </AutocompleteWrap>
          <AutocompleteWrap>
            <FilterLabel>{t.unitLabel}</FilterLabel>
            <Autocomplete
              name="filters.division"
              clearIcon={null}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              options={options.division}
              getOptionLabel={(option) => (option as (typeof options.division)[number]).title}
              renderOption={(props, option) => <li {...props}>{option.title}</li>}
              renderInput={(params) => <TextField {...params} placeholder={t.unitPlaceholder} />}
            />
          </AutocompleteWrap>
          <AutocompleteWrap>
            <FilterLabel>{t.divisionLabel}</FilterLabel>
            <Autocomplete
              name="filters.unit"
              clearIcon={null}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              options={options.unit}
              getOptionLabel={(option) => (option as (typeof options.unit)[number]).title}
              groupBy={(option) => option.category}
              renderOption={(props, option) => <li {...props}>{option.title}</li>}
              renderInput={(params) => <TextField {...params} placeholder={t.divisionPlaceholder} />}
            />
          </AutocompleteWrap>
          <AutocompleteWrap>
            <FilterLabel>{t.nameLabel}</FilterLabel>
            <Autocomplete
              name="filters.name"
              clearIcon={null}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              options={options.soldier}
              getOptionLabel={(option) => (option as (typeof options.soldier)[number]).name}
              renderOption={(props, option) => (
                <li {...props} className={`${props.className} multiselect`}>
                  {option.rank} - {option.name}
                </li>
              )}
              isVirtualList
              renderInput={(params) => <TextField {...params} placeholder={t.namePlaceholder} />}
            />
          </AutocompleteWrap>
        </FlexWrapper>
        <ButtonsFlexWrapper>
          <ResetBtn type="button" onClick={onResetBtnClick}>
            {t.resetBtnLabel}
          </ResetBtn>
          <ApplyBtn
            type="button"
            onClick={onApplyBtnClick}
            disabled={!search.formik.values.query && (!hasFilters || search.isFetching)}
          >
            {t.applyBtnLabel}
          </ApplyBtn>
        </ButtonsFlexWrapper>
      </Container>
    </Wrap>
  );
};

export default Filters;
